/**
 * PerformancePage — Smart Routing & Employee Composite Score leaderboard.
 *
 * Shown to STAS + GM + Deputy + Accountant. The page explains exactly HOW
 * the system rates each employee, so the team can see the AI is not a
 * black box. Each cell tooltips the underlying number.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNum } from "@/lib/format";

const WEIGHT_LABELS = {
  sla:      { ar: "الالتزام بالـ SLA", en: "SLA Compliance" },
  speed:    { ar: "السرعة", en: "Speed" },
  accuracy: { ar: "الدقة", en: "Accuracy" },
  load:     { ar: "خفّة الحمل", en: "Light Load" },
};

export default function PerformancePage() {
  const { theme, lang, dir } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [days, setDays] = useState(30);
  const css = makeStyles(theme);

  const load = async () => {
    setErr("");
    try {
      const r = await api.get(`/admin/performance?window_days=${days}`);
      setData(r.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [days]);

  if (err) return <div dir={dir} style={css.page}><div style={css.err}>{err}</div></div>;
  if (!data) return <div dir={dir} style={css.page}><div>…</div></div>;

  return (
    <div dir={dir} style={css.page} data-testid="performance-page">
      <header style={css.header}>
        <div>
          <h1 style={css.h1}>تقييم الأداء وتوزيع الذكاء</h1>
          <p style={css.sub}>
            هذه اللوحة تشرح بدقة كيف يُقيّم النظام كل موظف، وكيف يقرر الذكاء إلى أي موظف
            تُسند المعاملة الجديدة. أنت ترى الأرقام الفعلية من بيانات النظام — لا تخمين.
          </p>
        </div>
        <div style={css.winSelect}>
          <span style={{ fontSize: 12, color: theme.textMuted }}>الفترة:</span>
          {[7, 30, 90].map((d) => (
            <button key={d}
              onClick={() => setDays(d)}
              style={{ ...css.chip, ...(days === d ? css.chipActive : {}) }}
              data-testid={`window-${d}`}
            >
              {d} يوم
            </button>
          ))}
        </div>
      </header>

      {/* Weights explainer */}
      <section style={css.card} data-testid="weights-card">
        <h3 style={css.h3}>كيف تُحسب النتيجة النهائية؟</h3>
        <div style={css.weights}>
          {Object.entries(data.weights).map(([k, v]) => (
            <div key={k} style={css.weightCell}>
              <div style={css.weightLbl}>{WEIGHT_LABELS[k]?.ar || k}</div>
              <div style={css.weightVal}>{Math.round(v * 100)}%</div>
            </div>
          ))}
        </div>
        <p style={css.note}>
          النتيجة النهائية = (SLA × 35٪) + (السرعة × 25٪) + (الدقة × 25٪) + (خفّة الحمل × 15٪).
          وعند الإسناد التلقائي، يفوز الموظف ذو أعلى نتيجة وأقل حمل لحظي.
        </p>
      </section>

      {/* Leaderboard */}
      <section style={css.card}>
        <h3 style={css.h3}>الترتيب اللحظي</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={css.table} data-testid="leaderboard">
            <thead>
              <tr>
                <th>#</th>
                <th>الموظف</th>
                <th>الدور</th>
                <th>SLA</th>
                <th>السرعة</th>
                <th>الدقة</th>
                <th>خفّة الحمل</th>
                <th>الحمل النشط</th>
                <th>متوسط الإنجاز</th>
                <th>المُكتمَلة</th>
                <th style={{ background: theme.gold + "20" }}>النتيجة النهائية</th>
                <th>ترجيح الإسناد</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, idx) => (
                <tr key={r.user_id} data-testid={`row-${r.user_id}`}
                    style={r.is_management ? { opacity: 0.6 } : null}>
                  <td>{idx + 1}</td>
                  <td>
                    <div style={{ fontWeight: 800 }}>{r.name}</div>
                    <div style={{ fontSize: 10, color: theme.textMuted }}>{r.username}</div>
                  </td>
                  <td><span style={css.rolePill}>{r.role_code}</span></td>
                  <td><ScoreCell v={r.sla_score} /></td>
                  <td><ScoreCell v={r.speed_score} /></td>
                  <td><ScoreCell v={r.accuracy_score} /></td>
                  <td><ScoreCell v={r.load_score} /></td>
                  <td>{fmtNum(r.active_apps)}</td>
                  <td>{r.avg_completion_minutes != null ? `${fmtNum(Math.round(r.avg_completion_minutes))} د` : "—"}</td>
                  <td>{fmtNum(r.completed_apps)}</td>
                  <td><BigScore v={r.composite_score} theme={theme} /></td>
                  <td style={{ fontFamily: "monospace", color: theme.gold, fontWeight: 800 }}>{r.routing_weight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={css.note}>
          آخر تحديث: {new Date(data.as_of).toLocaleString("en-GB", { hour12: false, timeZone: "Asia/Riyadh" })} — الفترة {data.window_days} يوم.
        </p>
      </section>
    </div>
  );
}

function ScoreCell({ v }) {
  const color = v >= 90 ? "#16a34a" : v >= 70 ? "#f59e0b" : "#dc2626";
  return <span style={{ color, fontWeight: 800 }}>{v.toFixed(1)}</span>;
}

function BigScore({ v, theme }) {
  const color = v >= 85 ? "#16a34a" : v >= 65 ? "#f59e0b" : "#dc2626";
  return (
    <span style={{
      display: "inline-block", padding: "4px 10px", borderRadius: 8,
      background: color + "20", color, fontWeight: 900, fontSize: 13,
    }}>
      {v.toFixed(1)}
    </span>
  );
}

function makeStyles(theme) {
  return {
    page: { maxWidth: 1200, margin: "0 auto", padding: 16, color: theme.text },
    header: { display: "flex", justifyContent: "space-between", alignItems: "start", gap: 16, marginBottom: 16, flexWrap: "wrap" },
    h1: { fontSize: 22, fontWeight: 900, margin: 0, color: theme.gold },
    h3: { fontSize: 14, fontWeight: 800, marginTop: 0, marginBottom: 12, color: theme.gold },
    sub: { fontSize: 12, color: theme.textMuted, marginTop: 4, lineHeight: 1.7, maxWidth: 720 },
    winSelect: { display: "flex", alignItems: "center", gap: 8 },
    chip: { padding: "6px 12px", borderRadius: 999, border: `1px solid ${theme.borderSoft}`, background: "transparent", color: theme.text, cursor: "pointer", fontSize: 12 },
    chipActive: { background: theme.gold, color: "#0a0a0a", fontWeight: 800, border: `1px solid ${theme.gold}` },
    card: { padding: 16, borderRadius: 12, border: `1px solid ${theme.borderSoft}`, marginBottom: 16, background: theme.surfaceSubtle },
    weights: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 },
    weightCell: { padding: 10, borderRadius: 8, background: theme.surface, textAlign: "center" },
    weightLbl: { fontSize: 11, color: theme.textMuted },
    weightVal: { fontSize: 18, fontWeight: 900, color: theme.gold, marginTop: 4 },
    note: { fontSize: 11, color: theme.textMuted, margin: "8px 0 0", lineHeight: 1.6 },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
    rolePill: { fontSize: 10, padding: "2px 6px", borderRadius: 999, background: theme.surface, border: `1px solid ${theme.borderSoft}` },
    err: { color: "#dc2626", padding: 12, background: "#fee2e2", borderRadius: 8 },
  };
}
